import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import { useAlert } from '../../context/AlertContext';
import styles from './RegisterPage.module.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function RegisterPage({ onSwitch }) {
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    email: '',
    password: '',
    reenterPassword: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [errors, setErrors] = useState({});
  const [fadeout, setFadeout] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const { register } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();

  const { name, mobileNumber, email, password, reenterPassword } = formData;

  const onChange = useCallback((e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value })), []);

  const validate = useCallback(() => {
    let tempErrors = {};
    if (!name) tempErrors.name = 'Full name is required.';
    if (!mobileNumber.match(/^[0-9]{10}$/)) tempErrors.mobileNumber = 'Mobile number must be 10 digits.';
    if (!email.match(/^\S+@\S+\.\S+$/)) tempErrors.email = 'Email is not valid.';
    if (password.length < 6) tempErrors.password = 'Password must be at least 6 characters.';
    if (password !== reenterPassword) tempErrors.reenterPassword = 'Passwords do not match.';
    if (!termsAccepted) tempErrors.termsAccepted = 'You must accept the terms and privacy policy.';
    
    setErrors(tempErrors);
    setFadeout(false);
    setTimeout(() => setFadeout(true), 2000);
    return Object.keys(tempErrors).length === 0;
  }, [name, mobileNumber, email, password, reenterPassword, termsAccepted]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      showProgressBar();
      updateProgressBar(30);

      try {
        await new Promise(resolve => setTimeout(resolve, 250));
        updateProgressBar(60);
        await new Promise(resolve => setTimeout(resolve, 250));
        updateProgressBar(90);

        await register({ name, mobileNumber, email, password });
        showAlert('Registration successful! Please proceed with KYC.', 'success');
        navigate('/kyc');
      } catch (err) {
        const errorMsg = err.response?.data?.msg || 'An unexpected error occurred. Please try again.';
        showAlert(`Registration failed: ${errorMsg}`, 'error');
      } finally {
        hideProgressBar();
      }
    }
  };

  const togglePassword = useCallback(() => setShowPassword(!showPassword), [showPassword]);
  const toggleReenterPassword = useCallback(() => setShowReenterPassword(!showReenterPassword), [showReenterPassword]);

  return (
    <div className={styles['register-page-wrapper']}>
      <div className={styles['register-form']}>
        <h2>Create Your Account</h2>
        <div className={styles['form-intro']}>
          <p>Join {process.env.REACT_APP_NAME} to start trading</p>
        </div>
        <form onSubmit={onSubmit}>
          <div className={styles['form-group']}>
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={onChange}
              placeholder="Enter your full name"
            />
            {errors.name && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.name}</p>}
          </div>
          <div className={styles['form-group']}>
            <label htmlFor="mobileNumber">Mobile Number</label>
            <div className={styles['input-wrapper']}>
              <span className={styles['country-code']}>+91</span>
              <input
                type="text"
                id="mobileNumber"
                className={styles['mobile-input-field']}
                name="mobileNumber"
                value={mobileNumber}
                onChange={onChange}
                placeholder="Enter your mobile number"
              />
            </div>
            {errors.mobileNumber && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.mobileNumber}</p>}
          </div>
          <div className={styles['form-group']}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={onChange}
              placeholder="email@example.com"
            />
            {errors.email && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.email}</p>}
          </div>
          <div className={styles['form-group']}>
            <label htmlFor="password">Password</label>
            <div className={styles['input-wrapper']}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={onChange}
                placeholder="Create new password"
              />
              <span onClick={togglePassword} className={styles['eye-icon']}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {errors.password && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.password}</p>}
          </div>
          <div className={styles['form-group']}>
            <label htmlFor="reenterPassword">Re-enter Password</label>
            <div className={styles['input-wrapper']}>
              <input
                type={showReenterPassword ? 'text' : 'password'}
                id="reenterPassword"
                name="reenterPassword"
                value={reenterPassword}
                onChange={onChange}
                placeholder="Re-enter your password"
              />
              <span onClick={toggleReenterPassword} className={styles['eye-icon']}>
                {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {errors.reenterPassword && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.reenterPassword}</p>}
          </div>

          <div className={`${styles['form-group']} ${styles['checkbox-group']}`}>
            <input
              type="checkbox"
              id="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <label htmlFor="termsAccepted">
              I accept the <Link to="/terms" target="_blank">Terms and Conditions</Link>
            </label>
            {errors.termsAccepted && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.termsAccepted}</p>}
          </div>

          <button type="submit" className={styles['btn-register']}>Register</button>
        </form>
        <div className={styles['form-footer']}>
          <div className={styles['or-divider']}><span>OR</span></div>
          <p>
            Already have an account? {' '}
            {onSwitch ? (
              <span className={styles.switchLink} onClick={onSwitch}>Login</span>
            ) : (
              <Link to="/landing" state={{ openLogin: true }}>Login</Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
